import {
  type CalendarDate,
  endOfMonth,
  getLocalTimeZone,
  parseDate,
  startOfMonth,
  today,
} from '@internationalized/date';

import { type CalendarDateRange } from '../DateRangePicker';

export enum TransactionGroupType {
  DAY = 'day',
  MONTH = 'month',
  YEAR = 'year',
}

/**
 * Calculate the difference in days between two CalendarDate objects.
 * Uses epoch days for accurate calculation across different calendar systems.
 */
function daysBetween(start: CalendarDate, end: CalendarDate): number {
  // Convert both dates to UTC Date objects at midnight, then calculate epoch days
  const startEpochDays = Math.floor(start.toDate('UTC').getTime() / (1000 * 60 * 60 * 24));
  const endEpochDays = Math.floor(end.toDate('UTC').getTime() / (1000 * 60 * 60 * 24));
  return endEpochDays - startEpochDays;
}

/**
 * Determine how to group transactions based on the date range
 * - Less than 60 days: group by day
 * - Less than 2 years (730 days): group by month
 * - 2 years or more: group by year
 */
export function getTransactionGroupType(dateRange?: CalendarDateRange): TransactionGroupType {
  if (!dateRange) {
    return TransactionGroupType.MONTH;
  }

  const daysDiff = daysBetween(dateRange.start, dateRange.end);

  if (daysDiff < 60) {
    return TransactionGroupType.DAY;
  } else if (daysDiff < 730) {
    return TransactionGroupType.MONTH;
  }
  return TransactionGroupType.YEAR;
}

export interface IBarSizing {
  width: string;
  widthPx?: number;
  barCategoryGap: number;
  barSize?: number;
  shouldScroll: boolean;
}

/**
 * Calculate responsive sizing for bar charts
 * Determines bar size, gap, and whether scrolling is needed
 */
export function calculateBarSizing(params: {
  numBars: number;
  containerWidth: number;
  minBarWidth?: number;
  maxGap?: number;
  minGap?: number;
  yAxisWidth?: number;
  padding?: number;
  margins?: number;
}): IBarSizing {
  const {
    numBars,
    containerWidth,
    minBarWidth = 20,
    maxGap = 12,
    minGap = 4,
    yAxisWidth = 80,
    padding = 40,
    margins = 10,
  } = params;

  if (numBars === 0) {
    return {
      width: '100%',
      widthPx: undefined,
      barCategoryGap: maxGap,
      barSize: undefined,
      shouldScroll: false,
    };
  }

  const availableWidth = containerWidth - yAxisWidth - padding - margins;
  const minRequiredWidth = numBars * minBarWidth + (numBars - 1) * minGap;

  if (availableWidth < minRequiredWidth) {
    const neededWidth = minRequiredWidth + yAxisWidth + padding;
    return {
      width: '100%',
      widthPx: neededWidth,
      barCategoryGap: minGap,
      barSize: minBarWidth,
      shouldScroll: true,
    };
  }

  const extraSpace = availableWidth - minRequiredWidth;
  const maxGapGrowth = maxGap - minGap;
  const growthRatio = numBars + 4 * (numBars - 1);
  const growthToMaxGap = maxGapGrowth / 4;
  const spaceNeededForMaxGap = growthToMaxGap * numBars + maxGapGrowth * (numBars - 1);

  let barSize: number;
  let gap: number;

  if (extraSpace <= spaceNeededForMaxGap) {
    const growthPerBar = extraSpace / growthRatio;
    barSize = minBarWidth + growthPerBar;
    gap = minGap + growthPerBar * 4;
  } else {
    const remainingSpace = extraSpace - spaceNeededForMaxGap;
    const additionalBarGrowth = remainingSpace / numBars;
    barSize = minBarWidth + growthToMaxGap + additionalBarGrowth;
    gap = maxGap;
  }

  return {
    width: '100%',
    widthPx: undefined,
    barCategoryGap: Math.round(gap),
    barSize: Math.floor(barSize),
    shouldScroll: false,
  };
}

/**
 * Calculate the optimal interval for X-axis labels to prevent overlap
 * Returns the interval value for recharts (0 = show all, 1 = show every other, 2 = show every 3rd, etc.)
 */
export function calculateLabelInterval(params: {
  numDataPoints: number;
  containerWidth: number;
  estimatedLabelWidth?: number;
  yAxisWidth?: number;
  padding?: number;
  margins?: number;
}): number {
  const {
    numDataPoints: numBars,
    containerWidth,
    estimatedLabelWidth = 80, // Approximate width of "Jan 1" or "Jan" labels
    yAxisWidth = 80,
    padding = 40,
    margins = 10,
  } = params;

  if (numBars === 0) {
    return 0;
  }

  // Calculate available width for labels
  const availableWidth = containerWidth - yAxisWidth - padding - margins;

  // Calculate how much space each label would have if all were shown
  const spacePerLabel = availableWidth / numBars;

  // If there's enough space for all labels, show them all
  if (spacePerLabel >= estimatedLabelWidth) {
    return 0;
  }

  // Calculate how many labels we can fit
  const numLabelsThatFit = Math.floor(availableWidth / estimatedLabelWidth);

  // If we can't fit any labels, just show every nth label where n is large
  if (numLabelsThatFit <= 1) {
    // Try to show at least a few labels
    return Math.max(1, Math.ceil(numBars / 3) - 1);
  }

  // Calculate the interval needed to fit the labels
  // interval = floor((numBars - 1) / numLabelsThatFit)
  // This ensures we show approximately numLabelsThatFit labels
  const interval = Math.floor(numBars / numLabelsThatFit) - 1;

  return Math.max(0, interval);
}

/**
 * Get the date range for a clicked bar period based on the group type.
 * For month grouping: returns the month start to month end
 * For year grouping: returns the year start to year end
 * The end date is clamped to today to prevent future dates.
 *
 * Returns undefined for day grouping since drilling down to a single day doesn't make sense.
 */
export function getDateRangeForPeriod(period: string, groupType: TransactionGroupType): CalendarDateRange | undefined {
  if (groupType === TransactionGroupType.DAY) {
    // For day grouping, don't change date range (clicking on a single day doesn't make sense)
    return undefined;
  }

  const now = today(getLocalTimeZone());
  const periodDate = parseDate(period);

  let start: CalendarDate;
  let end: CalendarDate;

  if (groupType === TransactionGroupType.MONTH) {
    // For month grouping: set to month start and month end
    start = startOfMonth(periodDate);
    end = endOfMonth(periodDate);
  } else {
    // For year grouping: set to year start and year end
    start = startOfMonth(periodDate.set({ month: 1 }));
    end = endOfMonth(periodDate.set({ month: 12 }));
  }

  // Ensure end date doesn't exceed today
  if (end.compare(now) > 0) {
    end = now;
  }

  return { start, end };
}
